/* Gassas – flying-bottles hero (v5: galaxy + Christmas styles)
 * Scroll-driven fly-through: the camera moves forward through a looping depth field
 * of perfume bottles and stars (galaxy) or falling snow and warm lights (Christmas). Scroll position drives the camera; a slow idle drift
 * keeps space alive when the page is still. Only transform/opacity are animated.
 */
(() => {
  'use strict';

  // Tunables (exposed for live tuning in the browser console).
  const CFG = (window.GassasGalaxyCFG = Object.assign({
    D: 10,            // depth of one loop of space
    F: 1.25,          // focal length: scale = F / depth
    NEAR: 0.42,       // fully faded out closer than this
    NEAR_FADE: 1.15,  // starts fading out below this depth
    TRAVEL: 1.15,     // camera travel over the whole scroll, in loops
    spreadX: 0.92, spreadXm: 0.9, spreadY: 0.62,
    calmW: 0.3, calmWm: 0.42, calmH: 0.36, calmMin: 0.15,
    hazeMin: 0.3, ease: 5.5, streak: 0.035, maxScale: 2.4
  }, window.GassasGalaxyCFG || {}));

  // Hand-placed lanes (x, y) — kept away from the centre so the copy stays readable.
  const LANES = [
    [-0.92, -0.30], [0.88, 0.26], [-0.62, 0.62], [0.62, -0.62],
    [-1.22, 0.14], [1.16, -0.12], [-0.40, -0.78], [0.42, 0.80],
    [-0.98, -0.78], [1.00, 0.72], [-1.36, -0.46], [1.30, 0.48],
    [0.20, -0.95], [-0.22, 0.98], [1.45, -0.62], [-1.48, 0.70]
  ];

  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const smooth = (e0, e1, x) => {
    const t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const mod = (a, n) => ((a % n) + n) % n;
  // Small deterministic PRNG so the composition is identical on every load.
  const rng = (seed) => () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  class Galaxy {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('.gx__stage');
      this.canvas = root.querySelector('.gx__stars');
      this.ctx = this.canvas.getContext('2d');
      this.tip = root.querySelector('.gx__tip');
      this.panels = {
        intro: root.querySelector('[data-panel="intro"]'),
        mid: root.querySelector('[data-panel="mid"]'),
        outro: root.querySelector('[data-panel="outro"]')
      };
      this.hint = root.querySelector('.gx__hint');
      this.nebulae = [...root.querySelectorAll('.gx__nebula')];
      this.idleSpeed = (parseFloat(root.dataset.speed) || 0) * 0.035;
      this.mobileCount = parseInt(root.dataset.mobileCount, 10) || 9;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.xmas = root.dataset.style === 'christmas';

      this.cam = 0;
      this.camTarget = 0;
      this.idle = 0;
      this.vel = 0;
      this.mouse = { x: 0, y: 0, tx: 0, ty: 0 };
      this.visible = false;
      this.running = false;
      this.last = 0;
      this.t = 0;
      this.textVis = 1;

      this.setupBottles();
      this.resize();
      this.setupStars();
      this.bind();

      if (this.reduce) {
        root.classList.add('gx--static');
        this.cam = CFG.D * 0.32;
        this.renderFrame(0, true);
      } else {
        this.readScroll();
        this.cam = this.camTarget;
        this.renderFrame(0, true);
      }
    }

    setupBottles() {
      const { D } = CFG;
      const els = [...this.root.querySelectorAll('.gx-bottle')];
      const n = els.length || 1;
      const r = rng(1861);
      this.bottles = els.map((el, i) => {
        const lane = LANES[i % LANES.length];
        return {
          el,
          img: el.querySelector('img'),
          label: el.dataset.label || '',
          size: parseFloat(el.dataset.size) || 1,
          x: lane[0] + (r() - 0.5) * 0.12,
          y: lane[1] + (r() - 0.5) * 0.12,
          // spread evenly through the depth loop, with a little jitter
          z: (i / n) * D + (r() - 0.5) * (D / n) * 0.5 + 1.4,
          phase: r() * Math.PI * 2,
          spin: (r() - 0.5) * 10,      // base tilt in degrees
          wob: 2 + r() * 4,            // wobble amplitude in degrees
          live: false,
          z_index: -1
        };
      });
    }

    applyMobileLimit() {
      const mobile = this.w < 750;
      this.bottles.forEach((b, i) => {
        const hide = mobile && i >= this.mobileCount;
        b.hidden = hide;
        b.el.classList.toggle('is-mobile-hidden', hide);
      });
    }

    setupStars() {
      if (this.xmas) return this.setupSnow();
      const { D } = CFG;
      const area = this.w * this.h;
      const count = clamp(Math.round(area / 2600), 140, 620);
      const r = rng(42);
      const palette = ['255,255,255', '255,255,255', '255,255,255', '210,225,255', '255,232,196', '232,214,255'];
      this.stars = Array.from({ length: count }, () => ({
        x: (r() - 0.5) * 4.2,
        y: (r() - 0.5) * 3.2,
        z: r() * D,
        size: 0.35 + r() * r() * 1.4,
        tw: r() * Math.PI * 2,
        tws: 0.6 + r() * 2.2,
        c: palette[Math.floor(r() * palette.length)]
      }));
      // a handful of fixed far-away stars for the static background layer
      this.dust = Array.from({ length: Math.round(count * 0.6) }, () => ({
        x: r(), y: r(), a: 0.15 + r() * 0.45, s: 0.3 + r() * 0.7, tw: r() * 6.28
      }));
    }

    bind() {
      this.onScroll = () => { this.readScroll(); this.kick(); };
      this.onResize = () => { this.resize(); this.readScroll(); this.kick(true); };
      // capture phase so it also fires when the theme scrolls inside a wrapper element
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.kick(true); else this.stop();
      }, { rootMargin: '100px 0px' });
      this.io.observe(this.root);

      this.onVis = () => (document.hidden ? this.stop() : this.kick(true));
      document.addEventListener('visibilitychange', this.onVis);

      if (window.matchMedia('(hover: hover)').matches) {
        this.onMove = (e) => {
          const rect = this.stage.getBoundingClientRect();
          this.mouse.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
          this.mouse.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
          this.moveTip(e.clientX - rect.left, e.clientY - rect.top);
        };
        this.stage.addEventListener('pointermove', this.onMove, { passive: true });
        this.stage.addEventListener('pointerleave', () => { this.mouse.tx = 0; this.mouse.ty = 0; this.showTip(null); });
        this.bottles.forEach((b) => {
          b.el.addEventListener('pointerenter', () => this.showTip(b));
          b.el.addEventListener('pointerleave', () => this.showTip(null));
        });
      }
    }

    showTip(b) {
      if (!this.tip) return;
      this.tipFor = b;
      if (b && b.label) {
        this.tip.textContent = b.label;
        this.tip.classList.add('is-on');
      } else {
        this.tip.classList.remove('is-on');
      }
    }

    moveTip(x, y) {
      if (this.tip) this.tip.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -150%)`;
    }

    resize() {
      const rect = this.stage.getBoundingClientRect();
      this.w = rect.width || window.innerWidth;
      this.h = rect.height || window.innerHeight;
      this.dpr = Math.min(window.devicePixelRatio || 1, this.w < 750 ? 1.75 : 2);
      this.canvas.width = Math.round(this.w * this.dpr);
      this.canvas.height = Math.round(this.h * this.dpr);
      this.applyMobileLimit();
      if (this.stars) this.setupStars();
    }

    readScroll() {
      const rect = this.root.getBoundingClientRect();
      const span = Math.max(1, rect.height - this.h);
      this.progress = clamp(-rect.top / span, 0, 1);
      this.camTarget = this.progress * CFG.D * CFG.TRAVEL;
    }

    kick(force) {
      if (this.reduce) { if (force) this.renderFrame(0, true); return; }
      if (!this.visible || this.running || document.hidden) return;
      this.running = true;
      this.last = performance.now();
      const loop = (now) => {
        if (!this.running) return;
        const dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        this.renderFrame(dt);
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    }

    stop() {
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    renderFrame(dt, still) {
      this.t += dt;
      // eased camera: scroll target + slow idle drift
      this.idle += dt * this.idleSpeed;
      const target = this.camTarget + this.idle;
      const prev = this.cam;
      const k = still ? 1 : 1 - Math.exp(-dt * CFG.ease);
      this.cam += (target - this.cam) * k;
      const v = dt > 0 ? (this.cam - prev) / dt : 0;
      this.vel += (v - this.vel) * (still ? 1 : 1 - Math.exp(-dt * 8));

      this.mouse.x += (this.mouse.tx - this.mouse.x) * (still ? 1 : 1 - Math.exp(-dt * 3));
      this.mouse.y += (this.mouse.ty - this.mouse.y) * (still ? 1 : 1 - Math.exp(-dt * 3));

      this.drawStars();
      this.placeBottles();
      this.placeCopy();
    }

    project(x, y, d) {
      const s = CFG.F / d;
      const half = this.w * 0.5;
      const spreadX = this.w < 750 ? CFG.spreadXm : CFG.spreadX;
      const px = half + (x - this.mouse.x * 0.08) * s * this.w * spreadX;
      const py = this.h * 0.5 + (y - this.mouse.y * 0.06) * s * this.h * CFG.spreadY;
      return [px, py, s];
    }

    placeBottles() {
      const { D, NEAR, NEAR_FADE } = CFG;
      const t = this.t;
      for (const b of this.bottles) {
        if (b.hidden) continue;
        const d = mod(b.z - this.cam, D) || D;
        if (d < NEAR * 0.8) {
          if (b.shown !== false) { b.el.style.opacity = '0'; b.shown = false; }
          continue;
        }
        const bob = Math.sin(t * 0.55 + b.phase) * 0.025;
        const [px, py, s] = this.project(b.x, b.y + bob, d);
        const fadeIn = 1 - smooth(D * 0.72, D * 0.98, d);
        const fadeOut = smooth(NEAR, NEAR_FADE, d);
        const haze = CFG.hazeMin + (1 - CFG.hazeMin) * smooth(D * 0.9, D * 0.3, d);
        // keep a calm zone behind the copy: bottles passing the centre dim down
        const zx = Math.abs(px - this.w * 0.5) / (this.w * (this.w < 750 ? CFG.calmWm : CFG.calmW));
        const zy = Math.abs(py - this.h * 0.5) / (this.h * CFG.calmH);
        const zone = Math.max(zx, zy);
        const calmRaw = zone >= 1 ? 1 : CFG.calmMin + (1 - CFG.calmMin) * zone * zone;
        const calm = 1 - (1 - calmRaw) * this.textVis; // only calm while copy is on screen
        const alpha = fadeIn * fadeOut * haze * calm;
        const scale = Math.min(s, CFG.maxScale) * b.size;
        const rot = b.spin + Math.sin(t * 0.32 + b.phase) * b.wob;
        b.el.style.transform = `translate3d(${px.toFixed(1)}px, ${py.toFixed(1)}px, 0) scale(${scale.toFixed(4)}) rotate(${rot.toFixed(2)}deg)`;
        b.el.style.opacity = alpha.toFixed(3);
        b.shown = true;
        const zi = Math.round((D - d) * 100);
        if (zi !== b.z_index) { b.el.style.zIndex = zi; b.z_index = zi; }
        const live = alpha > 0.45 && s > 0.28;
        if (live !== b.live) { b.el.classList.toggle('is-live', live); b.live = live; }
      }
    }

    drawStars() {
      if (this.xmas) return this.drawSnow();
      const { D } = CFG;
      const ctx = this.ctx;
      const dpr = this.dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, this.w, this.h);
      const t = this.t;

      // static dust layer (parallax only with scroll progress)
      const shift = (this.progress || 0) * this.h * 0.08;
      for (const s of this.dust) {
        const a = s.a * (0.65 + 0.35 * Math.sin(t * 1.3 + s.tw));
        ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
        const y = mod(s.y * this.h - shift, this.h);
        ctx.fillRect(s.x * this.w, y, s.s, s.s);
      }

      // flying stars
      ctx.globalCompositeOperation = 'lighter';
      const streak = clamp(Math.abs(this.vel) * CFG.streak, 0, 0.9);
      ctx.lineCap = 'round';
      for (const st of this.stars) {
        const d = mod(st.z - this.cam, D) || D;
        if (d < 0.25) continue;
        const [px, py, s] = this.project(st.x, st.y, d);
        if (px < -40 || px > this.w + 40 || py < -40 || py > this.h + 40) continue;
        const near = 1 - d / D;
        const tw = 0.7 + 0.3 * Math.sin(t * st.tws + st.tw);
        const a = clamp(near * 1.15, 0, 1) * tw * smooth(D, D * 0.8, d);
        const r = st.size * (0.35 + s * 0.9);
        if (streak > 0.04) {
          const [qx, qy] = this.project(st.x, st.y, d + streak * (0.6 + near));
          ctx.strokeStyle = `rgba(${st.c},${(a * 0.85).toFixed(3)})`;
          ctx.lineWidth = Math.max(0.6, r);
          ctx.beginPath();
          ctx.moveTo(qx, qy);
          ctx.lineTo(px, py);
          ctx.stroke();
        } else {
          ctx.fillStyle = `rgba(${st.c},${a.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(px, py, Math.max(0.45, r), 0, 6.2832);
          ctx.fill();
        }
        if (r > 1.3 && a > 0.5) {
          // soft halo on the brightest, closest stars
          ctx.fillStyle = `rgba(${st.c},${(a * 0.12).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(px, py, r * 3.2, 0, 6.2832);
          ctx.fill();
        }
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    // ---------- Christmas: soft 3D snow + warm bokeh lights ----------
    sprite(rgb, size, core) {
      const c = document.createElement('canvas');
      c.width = c.height = size;
      const g = c.getContext('2d');
      const r = size / 2;
      const grad = g.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, `rgba(${rgb},1)`);
      grad.addColorStop(core, `rgba(${rgb},0.85)`);
      grad.addColorStop(Math.min(0.95, core + 0.25), `rgba(${rgb},0.25)`);
      grad.addColorStop(1, `rgba(${rgb},0)`);
      g.fillStyle = grad;
      g.fillRect(0, 0, size, size);
      return c;
    }

    setupSnow() {
      const { D } = CFG;
      const area = this.w * this.h;
      const count = clamp(Math.round(area / 3000), 120, 460);
      const r = rng(2412);
      if (!this.flakeSprite) {
        this.flakeSprite = this.sprite('255,255,255', 64, 0.35);
        this.bokehSprites = [
          this.sprite('255,205,130', 128, 0.55),  // warm gold
          this.sprite('255,170,95', 128, 0.55),   // amber
          this.sprite('235,95,80', 128, 0.55),    // soft red
          this.sprite('255,235,200', 128, 0.55),  // candle white
          this.sprite('150,210,165', 128, 0.55)   // pine green
        ];
      }
      this.stars = Array.from({ length: count }, () => ({
        x: (r() - 0.5) * 4.4,
        y: (r() - 0.5) * 3.4,
        z: r() * D,
        size: 0.8 + r() * r() * 2.8,
        fall: 0.05 + r() * 0.09,
        sway: 0.4 + r() * 0.9,
        amp: 0.02 + r() * 0.05,
        ph: r() * Math.PI * 2
      }));
      const n = this.w < 750 ? 14 : 24;
      const weights = [0, 0, 0, 1, 1, 2, 3, 3, 4];
      this.dust = Array.from({ length: n }, () => ({
        x: r(), y: r(),
        rad: 18 + r() * r() * 70,
        a: 0.12 + r() * 0.2,
        tw: r() * 6.28, tws: 0.4 + r() * 1.1,
        depth: 0.3 + r() * 0.7,
        s: weights[Math.floor(r() * weights.length)]
      }));
    }

    drawSnow() {
      const { D } = CFG;
      const ctx = this.ctx;
      const dpr = this.dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, this.w, this.h);
      const t = this.t;
      const p = this.progress || 0;

      // warm out-of-focus lights, far behind everything
      ctx.globalCompositeOperation = 'lighter';
      for (const b of this.dust) {
        const tw = 0.6 + 0.4 * Math.sin(t * b.tws + b.tw);
        const y = mod(b.y * this.h - p * this.h * 0.25 * b.depth, this.h + b.rad * 2) - b.rad;
        const x = b.x * this.w + Math.sin(t * 0.1 + b.tw) * 8;
        const rr = b.rad * (this.w < 750 ? 0.75 : 1);
        ctx.globalAlpha = b.a * tw;
        ctx.drawImage(this.bokehSprites[b.s], x - rr, y - rr, rr * 2, rr * 2);
      }

      // snow: falls in world space, flies past the camera as the page scrolls
      ctx.globalCompositeOperation = 'source-over';
      const span = 3.4;
      for (const f of this.stars) {
        const d = mod(f.z - this.cam, D) || D;
        if (d < 0.3) continue;
        const wy = mod(f.y + span / 2 + t * f.fall, span) - span / 2;
        const wx = f.x + Math.sin(t * f.sway + f.ph) * f.amp;
        const [px, py, s] = this.project(wx, wy, d);
        if (px < -30 || px > this.w + 30 || py < -30 || py > this.h + 30) continue;
        const near = 1 - d / D;
        const a = clamp(0.22 + near * 0.78, 0, 1) * smooth(D, D * 0.82, d) * smooth(0.3, 0.9, d);
        const rr = f.size * (1.2 + s * 3.2);
        ctx.globalAlpha = a * 0.9;
        ctx.drawImage(this.flakeSprite, px - rr, py - rr, rr * 2, rr * 2);
      }
      ctx.globalAlpha = 1;
    }

    placeCopy() {
      const p = this.progress || 0;
      const { intro, mid, outro } = this.panels;
      const setPanel = (el, o, y, scale) => {
        if (!el) return;
        el.style.opacity = o.toFixed(3);
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(4)})`;
        el.style.visibility = o < 0.01 ? 'hidden' : 'visible';
        const on = o > 0.6;
        if (on !== el._on) {
          el._on = on;
          el.classList.toggle('is-on', on);
          if (el !== intro) {
            el.setAttribute('aria-hidden', on ? 'false' : 'true');
            el.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', on ? '0' : '-1'));
          }
        }
      };
      if (this.reduce) { setPanel(intro, 1, 0, 1); this.textVis = 1; return; }

      const outIntro = smooth(0.08, 0.3, p);
      setPanel(intro, 1 - outIntro, -outIntro * 60, 1 + outIntro * 0.06);

      const midIn = smooth(0.32, 0.44, p) * (1 - smooth(0.6, 0.7, p));
      setPanel(mid, midIn, (1 - smooth(0.32, 0.44, p)) * 40 - smooth(0.6, 0.7, p) * 40, 0.96 + midIn * 0.04);

      const outroIn = smooth(0.76, 0.88, p);
      setPanel(outro, outroIn, (1 - outroIn) * 40, 0.96 + outroIn * 0.04);
      this.textVis = Math.max(1 - outIntro, mid ? midIn : 0, outro ? outroIn : 0);

      if (this.hint) this.hint.style.opacity = (1 - smooth(0.02, 0.1, p)).toFixed(3);
      // nebula parallax
      this.nebulae.forEach((n, i) => {
        n.style.translate = `0 ${(-p * (40 + i * 30)).toFixed(1)}px`;
      });
    }

    destroy() {
      this.stop();
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVis);
      if (this.io) this.io.disconnect();
    }
  }

  const instances = new WeakMap();
  const init = (root) => {
    if (instances.has(root)) return;
    const g = new Galaxy(root);
    instances.set(root, g);
    root._galaxy = g;
  };
  const boot = () => document.querySelectorAll('[data-galaxy]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-galaxy]');
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-galaxy]');
    if (root && instances.has(root)) { instances.get(root).destroy(); instances.delete(root); }
  });
})();
